import { splitOf } from '../../helpers/lengthSplit';

/**
 * How a lot's length is divided between orders and stock.
 *
 * The planned length is one number that answers two different questions, and
 * only one of them is actionable: how much is already somebody's, and how much
 * is still free to promise. A planner hunting for reservable metres was reading
 * that second figure off a row of per-client badges and doing the subtraction
 * in their head.
 *
 * The bar is there so it reads without arithmetic — a glance says "mostly
 * spoken for" or "mostly free" before any digit is parsed. Free metres are
 * green because they are the ones you can do something with.
 *
 * One derivation, used by both tables that show this: the launch split when the
 * job has one, the allocations when it does not. Two screens computing "free"
 * two ways would eventually disagree about the same product.
 */

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = n => Math.round(num(n)).toLocaleString();

const LengthSplit = ({ job, showTotal = true }) => {
  const { total, toOrders, toStock } = splitOf(job);
  if (total <= 0) return <span className="text-body-tertiary">—</span>;

  const pct = Math.round((toOrders / total) * 100);

  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: 130 }}>
      {showTotal && (
        <span className="fw-semibold fs-9">{fmt(total)} m</span>
      )}

      <div
        className="d-flex rounded overflow-hidden border"
        style={{ height: 6 }}
        title={`${fmt(toOrders)} m promised to orders · ${fmt(
          toStock
        )} m free to promise`}
      >
        <div className="bg-info" style={{ width: `${pct}%` }} />
        <div className="bg-success" style={{ width: `${100 - pct}%` }} />
      </div>

      <span className="fs-10 white-space-nowrap">
        {toOrders > 0 && (
          <span className="text-info fw-semibold">{fmt(toOrders)} order</span>
        )}
        {toOrders > 0 && toStock > 0 && (
          <span className="text-body-tertiary"> · </span>
        )}
        {toStock > 0 && (
          <span className="text-success fw-semibold">{fmt(toStock)} free</span>
        )}
        {toOrders <= 0 && toStock <= 0 && (
          <span className="text-body-tertiary">—</span>
        )}
      </span>
    </div>
  );
};

export default LengthSplit;
